import { withStyles } from '@material-ui/core';
import { useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, PrimaryButton, div } from "lqx-react";
import React, { useContext, useEffect } from "react";
import { ImportTemplateDetailsDto } from "../api/importTemplateDetails";
import { IngestionJob } from "../api/ingestionJob";
import { ImportTemplatesLookup } from "../api/lookups";
import { SignalRContext } from '../page-router';
import { BackButton, CancelButton, ErrorView, ErrorsContainer, RightAlignedButtons, WorkflowBody, WorkflowColumn, WorkflowFooter, WorkflowTitle } from "../shared";
import { UploadForm } from "./upload-form";

export const AddAssetsStep = ({
    manualAssetModel,
    assetName,
    allowedExtension,
    jobInfoProvider,
    $dataHolderId,
    userTemplates,
    $userTemplate,
    showDownloadTemplate,
    assetForm,
    showAssetTypes,
    next, back, cancel, job }:
    {
        manualAssetModel?,
        assetName: string,
        allowedExtension: string,
        jobInfoProvider,
        $dataHolderId?,
        userTemplates?: ImportTemplatesLookup,
        $userTemplate?,
        showDownloadTemplate?: boolean,
        showAssetTypes?: boolean,
        assetForm?: (asset) => JSX.Element,
        next?, back?, cancel?, job: IngestionJob
    }) => {
    const { notificationFunctions } = useContext(SignalRContext);
    const manualAsset = manualAssetModel ? useModel(manualAssetModel) : null;
    const $file = useLinked(null);
    const $doIngestionJobIO = useLinked(false);
    const $modifyExistingAssets = useLinked(false);
    const $mergeExistingAssets = useLinked(false);
    const $doSaveManualAssetIO = useLinked(false);
    const importTemplate = useModel(ImportTemplateDetailsDto);
    const isIngestionJobReady = useIO(
        async () => $doIngestionJobIO.isTruthy && await enqueueJobAndGoNext(),
        [$doIngestionJobIO.value]
    );
    const isManualAssetReady = useIO(
        async () => $doSaveManualAssetIO.isTruthy && await saveManualAssetAndGoNext(),
        [$doSaveManualAssetIO.value]
    );

    useEffect(() => {
        //add condition because it throws exceprion when we go to Mulptiple asset type upload page
        if (!showAssetTypes) {
            clearForm();
        }
    }, [true]);

    const enqueueJobAndGoNext = async () => {
        try {
            job.reset();
            notificationFunctions.current.setTimerNotificationAlert('uploaded a large import file');
            await job.enqueue({ params: { file: $file.value, modifyExistingAssets: $modifyExistingAssets.value, mergeExistingAssets: $mergeExistingAssets.value, ...jobInfoProvider() } });
            await job.waitForEnqueueToBeDone();
            if (!job.hasErrors())
                next(job.importJobId, true);
        }
        catch {
            job.reset();
        }
        finally {
            notificationFunctions.current.clearTimer();
            $doIngestionJobIO.set(false);
        }
    }

    const saveManualAssetAndGoNext = async () => {
        try {
            await manualAsset.save();
            job.set({ id: manualAsset.id, importJobId: manualAsset.importJobId });
            next();
        } finally {
            $doSaveManualAssetIO.set(false);
        }
    }

    const clearForm = () => {
        const defaults = manualAsset.defaults();
        return manualAsset.set(defaults);
    };

    const downloadFile = async (fileUrl, fileName) => {
        const file = await getFile(fileUrl, fileName);
        const dataUrl = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    const getFile = async (url, name) => {
        const response = await fetch(url);
        const blob = await response.blob()

        return new File([blob], name);
    }

    const onDownloadTemplate = async () => {
        const standardFileName = assetName.toLowerCase() + '_template.csv';
        const filePath = '/assets/files/' + standardFileName;
        downloadFile(filePath, "Import_template.csv");
    }

    const onDownloadMapping = async () => {
        if ($userTemplate.value && $userTemplate.value.id) {
            await importTemplate.get($userTemplate.value.id).then(d => downloadFile(d.url, d.originalFileName))
        };
    }

    return (isIngestionJobReady && isManualAssetReady) ?
        <>
            <WorkflowTitle>
                Step 2: Upload A File or Add Assets
            </WorkflowTitle>
            <WorkflowBody>
                <Columns>
                    <WorkflowColumn>
                        {job.hasErrors() && <ErrorsContainer><ErrorView>Failed to process the selected file.</ErrorView></ErrorsContainer>}
                        <UploadForm
                            label={`${assetName}`}
                            $file={$file}
                            $userTemplate={$userTemplate}
                            $dataHolderId={$dataHolderId}
                            userTemplates={userTemplates ? userTemplates.items.models : null}
                            showDownloadTemplate={showDownloadTemplate}
                            alowedExtensions={allowedExtension}
                            onDownloadTemplate={onDownloadTemplate}
                            onDownloadMapping={onDownloadMapping}
                            onUpload={$doIngestionJobIO.true}
                            showAssetTypes={showAssetTypes}
                            modifyExistingAssets={$modifyExistingAssets} 
                            mergeExistingAssets={$mergeExistingAssets} />
                    </WorkflowColumn>
                    {assetForm != null ? <WorkflowColumn>
                        <ManualAssetContainer>
                            <ManualAssetTitle>Manually enter one {assetName} </ManualAssetTitle>
                            {assetForm(manualAsset)}
                            <Flex row>
                                <ButtonWrapper>
                                    <Column>
                                        <ClearButton color="primary" onClick={clearForm}>Clear Form</ClearButton>
                                    </Column>
                                    <Column>
                                        <PrimaryButtonStyled onClick={$doSaveManualAssetIO.true}>Save {assetName}</PrimaryButtonStyled>
                                    </Column>
                                </ButtonWrapper>
                            </Flex>
                        </ManualAssetContainer>
                    </WorkflowColumn> : null
                    }
                </Columns>
            </WorkflowBody>
            <WorkflowFooter>
                <Columns>
                    <Column>
                        <CancelButton color="primary" onClick={cancel}>Cancel</CancelButton>
                    </Column>
                    <Column>
                        <RightAlignedButtons>
                            <BackButton color="primary" onClick={back}>Back: Choose Asset Type</BackButton>
                        </RightAlignedButtons>
                    </Column>
                </Columns>
            </WorkflowFooter>
        </> : <>
            <Flex center>
                <Loading />
            </Flex>
            <Flex center>
                {job.status && <WorkflowTitle>{job.status}</WorkflowTitle>}
            </Flex>
        </>
};

const ManualAssetTitle = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '20px',
    alignItems: 'center',
    color: '#4197d6',
    fontWeight: 600,
    marginBottom: '15px'
});

const ManualAssetContainer = div({
    background: '#FFFFFF',
    border: '1px solid #E2F0F7',
    borderRadius: '4px',
    padding: '30px',
    marginBottom: '50px',
});

export const ClearButton = withStyles({
    root: {
        margin: 0,
    }
})(Button);

export const PrimaryButtonStyled = withStyles({
    root: {
        margin: 0,
    }
})(PrimaryButton);

export const ButtonWrapper = withStyles({
    root: {
        marginTop: 20,
    }
})(Columns);